import React, { useMemo, useState } from 'react';
import { Alert, Button, InputNumber, Modal, Select, Typography } from 'antd';
import { CalculatorOutlined, RightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatLocalizedQuantity } from '../../../utils/numberFormatting';
import { formatUnit } from '../../../utils/units';
import {
  asQuantityUnit,
  buildQuantitySetFromPair,
  COMMERCIAL_PRICING_CONVERSION_UNITS,
  COMMERCIAL_UNITS,
  CommercialQuantityValue,
  convertCommercialQuantity,
  convertCommercialQuantityUsingSnapshot,
  getCommercialPairMismatch,
  getFractionalPieces,
  INVOICE_PRINT_TOLERANCE,
  getQuantitySetValue,
  hasPersistedQuantitySet,
  isAllocationUnit,
  quantitySetFromFields,
  quantitySetToFields,
  ACTIVE_QUANTITY_UNITS,
  quantitySetForAppliedPair,
  quantitySetForEnteredAmount,
  roundCommercialQuantity,
} from '../../../utils/commercialQuantity';
import type { QuantityUnit } from '../../../utils/commercialQuantity';
import type { AllocationUnit, ConversionSource } from '../../../utils/dimensionConversion';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../../../utils/polishNumberInput';
import './CommercialQuantityEditor.css';

type ConversionMode = 'conversion' | 'none';
/** Whole pieces only (owner 2026-10-06): a fractional result has no „keep” option. */
type PieceRounding = 'down' | 'up' | null;

interface CommercialQuantityEditorProps extends ConversionSource {
  value: CommercialQuantityValue;
  onChange: (value: CommercialQuantityValue) => void;
  maxPhysicalQuantity: number;
  maxPricingQuantity: number;
  disabled?: boolean;
  compact?: boolean;
  /**
   * Initial unit for legacy callers that have not persisted a line unit yet.
   * Once a value exists, `value.physicalUnit` is authoritative for this line.
   */
  physicalUnit?: QuantityUnit;
  /**
   * Keep the physical stock quantity fixed. Used by PZ lines traced to a
   * supplier order: the operator may change the commercial unit and price
   * basis, but the received quantity must continue to match its source line.
   */
  lockPhysicalQuantity?: boolean;
  /** Optional context-specific subset of dimensional conversion targets. */
  pricingConversionUnits?: readonly AllocationUnit[];
  /** Optional context-specific units the operator may enter on the left side. */
  sourceUnitOptions?: readonly QuantityUnit[];
  triggerOnly?: boolean;
  /** `triggerOnly` as a small text link (inside a table cell or a form row) instead of a button. */
  triggerLink?: boolean;
  triggerLabel?: React.ReactNode;
  lockConversionMode?: boolean;
  lockPricingUnit?: boolean;
  modalDescription?: React.ReactNode;
  modalTitle?: React.ReactNode;
  allowOutOfBounds?: boolean;
  outOfBoundsMessage?: React.ReactNode;
  allowZero?: boolean;
  onZero?: () => void;
  /**
   * The amount is printed on a document (invoice line, 2026-10-22): rounding never
   * changes it. Within INVOICE_PRINT_TOLERANCE of whole pieces it is those pieces;
   * a real fractional piece is applied as printed (the invoice line offers rounding).
   */
  printedAmount?: boolean;
}

const positive = (value: unknown, fallback = 1) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

export const getModalPopupContainer = (triggerNode: HTMLElement): HTMLElement => (
  triggerNode.closest<HTMLElement>('.ant-modal-content')
  || triggerNode.closest<HTMLElement>('.ant-modal')
  || document.body
);

const CommercialQuantityEditor: React.FC<CommercialQuantityEditorProps> = ({
  value,
  onChange,
  maxPhysicalQuantity,
  maxPricingQuantity,
  disabled = false,
  compact = false,
  physicalUnit = 'pcs',
  lockPhysicalQuantity = false,
  pricingConversionUnits: pricingConversionUnitsProp = COMMERCIAL_PRICING_CONVERSION_UNITS,
  sourceUnitOptions: sourceUnitOptionsProp = ACTIVE_QUANTITY_UNITS,
  triggerOnly = false,
  triggerLink = false,
  triggerLabel,
  lockConversionMode = false,
  lockPricingUnit = false,
  modalDescription,
  modalTitle,
  allowOutOfBounds = false,
  outOfBoundsMessage,
  allowZero = false,
  onZero,
  printedAmount = false,
  width,
  length,
  height,
  thickness,
  countInPiecesOnly = false,
}) => {
  const { t, i18n } = useTranslation(['orders', 'common']);
  // „Liczymy tylko w sztukach” (migration 196): a new line of such a product
  // is entered in szt. only; a saved line keeps its own unit readable.
  const piecesOnly = countInPiecesOnly === true;
  const sourceUnitOptions = useMemo<readonly QuantityUnit[]>(() => (piecesOnly
    ? Array.from(new Set<QuantityUnit>(['pcs', asQuantityUnit(value.physicalUnit, 'pcs')]))
    : sourceUnitOptionsProp), [piecesOnly, sourceUnitOptionsProp, value.physicalUnit]);
  const pricingConversionUnits = useMemo<readonly AllocationUnit[]>(
    () => (piecesOnly ? [] : pricingConversionUnitsProp),
    [piecesOnly, pricingConversionUnitsProp],
  );
  // A few isolated consumers/tests provide only `t`. Polish is the product
  // language and is also the safe fallback during i18n bootstrap.
  const language = i18n?.language || 'pl';
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<ConversionMode>('none');
  const [pricingUnit, setPricingUnit] = useState<AllocationUnit>('m2');
  const [priceUnit, setPriceUnit] = useState<QuantityUnit>('pcs');
  const [plainUnit, setPlainUnit] = useState<QuantityUnit>('pcs');
  const [amount, setAmount] = useState(1);
  const [pieceRounding, setPieceRounding] = useState<PieceRounding>(null);
  const [editorDirty, setEditorDirty] = useState(false);

  const dimensions = useMemo(
    () => ({ width, length, height, thickness, countInPiecesOnly: piecesOnly }),
    [height, length, piecesOnly, thickness, width],
  );
  const hasStoredQuantitySet = useMemo(
    () => hasPersistedQuantitySet(value as unknown as Record<string, unknown>),
    [value],
  );
  const storedQuantitySet = useMemo(
    () => hasStoredQuantitySet
      ? quantitySetFromFields(value as unknown as Record<string, unknown>)
      : null,
    [hasStoredQuantitySet, value],
  );
  const persistedQuantitySet = useMemo(
    () => buildQuantitySetFromPair(value, dimensions),
    [dimensions, value],
  );
  const displayConversionUnit = value.conversionUnit
    ?? (value.physicalUnit !== value.pricingUnit ? value.pricingUnit : null);
  const derivedDisplayConversionQuantity = useMemo(() => {
    if (!displayConversionUnit || displayConversionUnit === value.physicalUnit) return undefined;
    if (!isAllocationUnit(value.physicalUnit) || !isAllocationUnit(displayConversionUnit)) return undefined;
    if (hasStoredQuantitySet) {
      return getQuantitySetValue(storedQuantitySet!, displayConversionUnit) ?? undefined;
    }
    return convertCommercialQuantity(
      value.physicalQuantity,
      value.physicalUnit,
      displayConversionUnit,
      dimensions,
    ).value ?? undefined;
  }, [dimensions, displayConversionUnit, hasStoredQuantitySet, storedQuantitySet, value.physicalQuantity, value.physicalUnit]);
  const displayConversionQuantity = hasStoredQuantitySet
    ? derivedDisplayConversionQuantity
    : value.conversionQuantity
      ?? (displayConversionUnit === value.pricingUnit ? value.pricingQuantity : undefined)
      ?? derivedDisplayConversionQuantity;
  const isConverted = Boolean(displayConversionUnit && displayConversionUnit !== value.physicalUnit);
  const legacyUnitReadOnly = !isAllocationUnit(value.physicalUnit);
  // Converting a unit into itself is not a conversion. Every dimensional unit
  // can be the entered source and every other dimensional unit can be its view.
  const activePhysicalUnit = plainUnit;
  const canConvertPhysicalUnit = isAllocationUnit(activePhysicalUnit);
  const pricingUnitOptions = useMemo(
    () => canConvertPhysicalUnit
      ? pricingConversionUnits.filter((unit) => (
        unit !== activePhysicalUnit
        && (!storedQuantitySet || getQuantitySetValue(storedQuantitySet, unit) !== null)
      ))
      : [],
    [activePhysicalUnit, canConvertPhysicalUnit, pricingConversionUnits, storedQuantitySet],
  );
  const activeConversionUnit = pricingUnitOptions.includes(pricingUnit)
    ? pricingUnit
    : (pricingUnitOptions[0] ?? 'm2');

  const openEditor = () => {
    // The persisted line pair is the only initial state. Catalog metadata is
    // deliberately not consulted: choosing m³ here means the amount below is
    // entered in m³, regardless of how this catalog item was created years ago.
    const storedConversionUnit = value.conversionUnit
      ?? (value.pricingUnit !== value.physicalUnit ? value.pricingUnit : null);
    const nextPhysicalUnit = asQuantityUnit(value.physicalUnit, physicalUnit);
    const nextCanConvertPhysicalUnit = isAllocationUnit(nextPhysicalUnit);
    const nextPricingUnitOptions = nextCanConvertPhysicalUnit
      ? pricingConversionUnits.filter((unit) => (
        unit !== nextPhysicalUnit
        && (!storedQuantitySet || getQuantitySetValue(storedQuantitySet, unit) !== null)
      ))
      : [];
    const nextMode: ConversionMode = nextCanConvertPhysicalUnit && nextPricingUnitOptions.length > 0 && (lockConversionMode || (value.physicalUnit === nextPhysicalUnit
      && storedConversionUnit !== null
      && storedConversionUnit !== value.physicalUnit))
      ? 'conversion'
      : 'none';
    const fallbackPricingUnit = nextPricingUnitOptions[0] ?? 'm2';
    const nextConversionUnit = (
      storedConversionUnit
        && isAllocationUnit(storedConversionUnit)
        && nextPricingUnitOptions.includes(storedConversionUnit)
        ? storedConversionUnit
        : fallbackPricingUnit
    );
    setMode(nextMode);
    setPricingUnit(nextConversionUnit);
    const storedPriceUnit = asQuantityUnit(value.pricingUnit, nextPhysicalUnit);
    setPriceUnit(
      storedQuantitySet && isAllocationUnit(storedPriceUnit)
        && getQuantitySetValue(storedQuantitySet, storedPriceUnit) === null
        ? nextPhysicalUnit
        : storedPriceUnit,
    );
    setPlainUnit(nextPhysicalUnit);
    setAmount(positive(value.physicalQuantity));
    setPieceRounding(null);
    setEditorDirty(false);
    setOpen(true);
  };

  // A saved line converts its saved amount through its stored set (the same goods
  // re-expressed); a CHANGED amount converts from the line's own dimensions, exactly
  // as the backend validates the pair (utils/commercialQuantity quantitySetForEnteredAmount).
  const amountSet = useMemo(
    () => (isAllocationUnit(activePhysicalUnit)
      ? quantitySetForEnteredAmount(storedQuantitySet, positive(amount), activePhysicalUnit, dimensions)
      : storedQuantitySet),
    [activePhysicalUnit, amount, dimensions, storedQuantitySet],
  );
  // Whole-piece rounding always produces a new amount: its options come from the dimensions too.
  const roundingSet = useMemo(
    () => (isAllocationUnit(activePhysicalUnit)
      ? quantitySetForEnteredAmount(storedQuantitySet, null, activePhysicalUnit, dimensions)
      : storedQuantitySet),
    [activePhysicalUnit, dimensions, storedQuantitySet],
  );

  // One whole-piece rule with the ZK/ZD tables (`getFractionalPieces`): a changed amount
  // whose nearest whole szt. forms a pair the backend accepts (40,046 m³ = 809 szt. of a
  // 600 × 2500 × 33 mm board) IS that whole number — shown and applied as 809 szt., never
  // offered rounding. Null when the amount is fractional or no snapping is needed.
  const acceptedWholePieces = useMemo(() => {
    if (!editorDirty || lockPhysicalQuantity) return null;
    if (activePhysicalUnit !== 'm2' && activePhysicalUnit !== 'm3') return null;
    const exactPieces = convertCommercialQuantityUsingSnapshot(
      positive(amount),
      activePhysicalUnit,
      'pcs',
      dimensions,
      amountSet,
    ).exactValue;
    if (exactPieces === null || !Number.isFinite(exactPieces)) return null;
    const whole = Math.round(exactPieces);
    if (whole <= 0 || whole === exactPieces) return null;
    if (printedAmount) {
      const printed = convertCommercialQuantityUsingSnapshot(whole, 'pcs', activePhysicalUnit, dimensions, amountSet).exactValue;
      return printed !== null && Math.abs(printed - positive(amount)) <= INVOICE_PRINT_TOLERANCE[activePhysicalUnit] + 1e-9
        ? whole
        : null;
    }
    return getFractionalPieces(positive(amount), activePhysicalUnit, dimensions) === null ? whole : null;
  }, [activePhysicalUnit, amount, amountSet, dimensions, editorDirty, lockPhysicalQuantity, printedAmount]);

  const baseResult = useMemo(() => {
    const physicalQuantity = positive(amount);
    const pristinePersistedPair = !editorDirty
      && activePhysicalUnit === value.physicalUnit
      && roundCommercialQuantity(physicalQuantity) === roundCommercialQuantity(value.physicalQuantity);
    const displayConversion = mode === 'conversion'
      ? convertCommercialQuantityUsingSnapshot(
        physicalQuantity,
        activePhysicalUnit as AllocationUnit,
        activeConversionUnit,
        dimensions,
        amountSet,
      )
      : null;
    const priceConversion = priceUnit === activePhysicalUnit
      ? { value: physicalQuantity, error: null }
      : isAllocationUnit(activePhysicalUnit) && isAllocationUnit(priceUnit)
        ? convertCommercialQuantityUsingSnapshot(physicalQuantity, activePhysicalUnit, priceUnit, dimensions, amountSet)
        : { value: null, error: 'unsupported-unit-pair' };
    const wholePiecesConversion = acceptedWholePieces !== null && activeConversionUnit === 'pcs';
    return {
      physicalQuantity,
      physicalUnit: activePhysicalUnit,
      pricingQuantity: pristinePersistedPair && priceUnit === value.pricingUnit
        ? value.pricingQuantity
        : acceptedWholePieces !== null && priceUnit === 'pcs'
          ? acceptedWholePieces
          : priceConversion.value ?? 0,
      pricingUnit: priceUnit,
      conversionQuantity: pristinePersistedPair
        && value.conversionUnit === activeConversionUnit
        && value.conversionQuantity !== undefined
        ? value.conversionQuantity
        : wholePiecesConversion && displayConversion
          ? acceptedWholePieces
          : displayConversion?.value ?? undefined,
      conversionExactQuantity: pristinePersistedPair
        && value.conversionUnit === activeConversionUnit
        && value.conversionQuantity !== undefined
        ? value.conversionQuantity
        : wholePiecesConversion && displayConversion
          ? acceptedWholePieces
          : displayConversion?.exactValue ?? undefined,
      conversionUnit: mode === 'conversion' ? activeConversionUnit : null,
      error: displayConversion?.error || priceConversion.error,
    };
  }, [acceptedWholePieces, activeConversionUnit, activePhysicalUnit, amount, amountSet, dimensions, editorDirty, mode, priceUnit, value]);

  const fractionalPieceRounding = useMemo(() => {
    const sourceCanProducePieces = activePhysicalUnit === 'm2' || activePhysicalUnit === 'm3';
    const pristinePieces = !editorDirty && value.conversionUnit === 'pcs'
      ? value.conversionQuantity
      : (!editorDirty && value.pricingUnit === 'pcs' ? value.pricingQuantity : undefined);
    const pieceConversion = sourceCanProducePieces
      ? convertCommercialQuantityUsingSnapshot(
        positive(amount),
        activePhysicalUnit,
        'pcs',
        dimensions,
        amountSet,
      )
      : null;
    const exactPieces = Number(pristinePieces ?? pieceConversion?.exactValue);
    const hasFraction = Number.isFinite(exactPieces)
      && Math.abs(exactPieces - Math.round(exactPieces)) > 0.0001;
    if (
      lockPhysicalQuantity
      || printedAmount
      || acceptedWholePieces !== null
      || !sourceCanProducePieces
      || baseResult.error
      || !hasFraction
    ) return null;

    const buildOption = (pieces: number) => {
      if (pieces <= 0) return null;
      const source = convertCommercialQuantityUsingSnapshot(
        pieces,
        'pcs',
        activePhysicalUnit,
        dimensions,
        roundingSet,
      );
      const pricing = priceUnit === 'pcs'
        ? { value: pieces, error: null }
        : convertCommercialQuantityUsingSnapshot(pieces, 'pcs', priceUnit as AllocationUnit, dimensions, roundingSet);
      if (source.error || pricing.error || source.value === null || pricing.value === null) return null;
      return {
        pieces,
        physicalQuantity: source.value,
        pricingQuantity: pricing.value,
      };
    };

    return {
      exactPieces,
      down: buildOption(Math.floor(exactPieces)),
      up: buildOption(Math.ceil(exactPieces)),
    };
  }, [
    acceptedWholePieces,
    activePhysicalUnit,
    amount,
    amountSet,
    baseResult.error,
    dimensions,
    editorDirty,
    lockPhysicalQuantity,
    priceUnit,
    printedAmount,
    roundingSet,
    value.conversionQuantity,
    value.conversionUnit,
    value.pricingQuantity,
    value.pricingUnit,
  ]);

  const result = useMemo(() => {
    if (!fractionalPieceRounding || !pieceRounding) return baseResult;
    const selected = fractionalPieceRounding[pieceRounding];
    if (!selected) return baseResult;
    const roundedDisplayConversion = mode === 'conversion'
      ? convertCommercialQuantityUsingSnapshot(
        selected.pieces,
        'pcs',
        activeConversionUnit,
        dimensions,
        roundingSet,
      )
      : null;
    return {
      ...baseResult,
      physicalQuantity: selected.physicalQuantity,
      pricingQuantity: selected.pricingQuantity,
      conversionQuantity: mode === 'conversion'
        ? roundedDisplayConversion?.value ?? baseResult.conversionQuantity
        : undefined,
      conversionExactQuantity: mode === 'conversion'
        ? roundedDisplayConversion?.exactValue ?? baseResult.conversionExactQuantity
        : undefined,
    };
  }, [activeConversionUnit, baseResult, dimensions, fractionalPieceRounding, mode, pieceRounding, roundingSet]);

  const formatQuantity = (quantity: number) => formatLocalizedQuantity(quantity, language);

  const amountMaximum = Math.max(maxPhysicalQuantity, maxPricingQuantity);
  const isZeroAmount = allowZero && Boolean(onZero) && Number(amount) === 0;
  const resultOutOfBounds = result.physicalQuantity > maxPhysicalQuantity
    || result.pricingQuantity > maxPricingQuantity
    || Number(result.conversionQuantity || 0) > maxPricingQuantity;
  // The pair about to be applied, checked with the backend rule (frontend mirror) — only
  // when the operator changed something; a saved pair opened unchanged is not judged here.
  const pairMismatch = useMemo(() => {
    if (!editorDirty || result.error || !isAllocationUnit(result.physicalUnit)) return null;
    const selectedRoundedPieces = pieceRounding ? fractionalPieceRounding?.[pieceRounding]?.pieces ?? null : null;
    // A printed amount's whole pieces were already accepted within the print
    // tolerance (717,19 m² = 250 szt.); the 0,0005 pair check does not re-judge them.
    const printedPieces = (unit: unknown, quantity: unknown) => printedAmount && acceptedWholePieces !== null
      && unit === 'pcs' && Number(quantity) === acceptedWholePieces;
    const pricingTrusted = printedPieces(result.pricingUnit, result.pricingQuantity);
    return getCommercialPairMismatch({
      physicalQuantity: result.physicalQuantity,
      physicalUnit: result.physicalUnit,
      pricingQuantity: pricingTrusted ? result.physicalQuantity : result.pricingQuantity,
      pricingUnit: pricingTrusted ? result.physicalUnit : result.pricingUnit,
      ...(mode === 'conversion'
        ? (printedPieces(result.conversionUnit, result.conversionQuantity)
          ? {}
          : { conversionQuantity: result.conversionQuantity, conversionUnit: result.conversionUnit })
        : selectedRoundedPieces !== null ? { conversionQuantity: selectedRoundedPieces, conversionUnit: 'pcs' as const } : {}),
    }, dimensions);
  }, [acceptedWholePieces, dimensions, editorDirty, fractionalPieceRounding, mode, pieceRounding, printedAmount, result]);
  // A changed amount with a fractional piece is applied only rounded down or up; a saved
  // line opened unchanged keeps its saved amount (it stays readable and saveable as it is).
  const roundingMissing = editorDirty && Boolean(fractionalPieceRounding)
    && !(pieceRounding && fractionalPieceRounding?.[pieceRounding]);
  const canApply = !legacyUnitReadOnly && (isZeroAmount || (!result.error
    && !pairMismatch
    && !roundingMissing
    && (!resultOutOfBounds || allowOutOfBounds)
    && result.physicalQuantity > 0
    && result.pricingQuantity > 0
    && result.physicalQuantity <= amountMaximum));

  const handleApply = () => {
    if (!canApply) return;
    if (isZeroAmount) {
      onZero?.();
      setOpen(false);
      return;
    }
    const selectedRoundedPieces = pieceRounding
      ? fractionalPieceRounding?.[pieceRounding]?.pieces ?? null
      : null;
    const appliedValue: CommercialQuantityValue = {
      physicalQuantity: roundCommercialQuantity(result.physicalQuantity),
      physicalUnit: result.physicalUnit,
      pricingQuantity: roundCommercialQuantity(result.pricingQuantity),
      pricingUnit: result.pricingUnit,
      ...(mode === 'conversion'
        ? {
          conversionQuantity: roundCommercialQuantity(result.conversionQuantity || 0),
          conversionUnit: result.conversionUnit,
        }
        : selectedRoundedPieces !== null
          ? {
            conversionQuantity: roundCommercialQuantity(selectedRoundedPieces),
            conversionUnit: 'pcs',
          }
          : { conversionUnit: null }),
    };
    // Unchanged amount → the stored set (same goods); changed amount → a new set from the
    // accepted pair and the line's own dimensions (what the backend validates).
    const appliedSet = storedQuantitySet && isAllocationUnit(appliedValue.physicalUnit)
      ? quantitySetForAppliedPair(storedQuantitySet, appliedValue, dimensions)
      : buildQuantitySetFromPair(appliedValue, dimensions);
    onChange({
      ...appliedValue,
      // Existing lines propagate their canonical snapshot through the editor.
      // New drafts keep the legacy callback shape; their owning adapter builds
      // the first set from the accepted pair.
      ...(storedQuantitySet && appliedSet ? quantitySetToFields(appliedSet) : {}),
    });
    setOpen(false);
  };

  return (
    <>
      {triggerOnly ? (!disabled && (
        <Button type={triggerLink ? 'link' : 'default'} size="small" icon={<CalculatorOutlined />} onClick={openEditor}>
          {triggerLabel || t('orders:unitConversion.convert')}
        </Button>
      )) : <div className={`commercial-quantity${compact ? ' commercial-quantity--compact' : ''}`}>
        {isConverted ? (
          <div className="commercial-quantity__chain">
            <div><span>{t('orders:unitConversion.physicalQuantity')}</span><strong>{formatQuantity(value.physicalQuantity)} {formatUnit(value.physicalUnit, language)}</strong></div>
            <RightOutlined />
            <div className="commercial-quantity__pricing"><span>{t('orders:unitConversion.convertedQuantity')}</span><strong>{displayConversionQuantity == null ? '—' : formatQuantity(displayConversionQuantity)} {formatUnit(displayConversionUnit ?? value.pricingUnit, language)}</strong></div>
          </div>
        ) : (
          <strong className="commercial-quantity__plain">
            {formatQuantity(value.physicalQuantity)} {formatUnit(value.physicalUnit, language)}
          </strong>
        )}
        {!disabled && (
          <Button type="link" size="small" icon={<CalculatorOutlined />} onClick={openEditor}>
            {isConverted ? t('orders:unitConversion.change') : t('orders:unitConversion.configure')}
          </Button>
        )}
      </div>}

      <Modal
        open={open}
        width={720}
        title={modalTitle || t('orders:unitConversion.title')}
        okText={t('orders:unitConversion.apply')}
        cancelText={t('common:cancel')}
        okButtonProps={{ disabled: !canApply }}
        onOk={handleApply}
        onCancel={() => setOpen(false)}
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary" className="commercial-quantity-modal__intro">
          {modalDescription || t('orders:unitConversion.description')}
          {piecesOnly ? (
            <span className="commercial-quantity-modal__pieces-only" data-testid="conversion-pieces-only">
              {' '}{t('orders:unitConversion.countInPiecesOnly')}
            </span>
          ) : null}
        </Typography.Paragraph>

        {lockPhysicalQuantity && (
          <Alert
            type="info"
            showIcon
            message={t('orders:unitConversion.physicalQuantityLocked')}
          />
        )}

        {legacyUnitReadOnly && (
          <Alert
            type="warning"
            showIcon
            message={t('orders:unitConversion.legacyUnitReadOnly')}
          />
        )}

        <div className="commercial-quantity-modal__simple-grid">
          <label>
            <span>{t('orders:unitConversion.baseUnit')}</span>
            <Select
              data-testid="conversion-base-unit"
              value={activePhysicalUnit}
              disabled={legacyUnitReadOnly}
              getPopupContainer={getModalPopupContainer}
              options={(legacyUnitReadOnly
                ? [value.physicalUnit]
                : sourceUnitOptions.filter((unit) => (
                  !hasStoredQuantitySet
                  || !isAllocationUnit(unit)
                  || getQuantitySetValue(storedQuantitySet!, unit) !== null
                )))
                .map((unit) => ({ value: unit, label: formatUnit(unit, language) }))}
              onChange={(next) => {
                const previousUnit = activePhysicalUnit;
                const persistedPreviousAmount = persistedQuantitySet && isAllocationUnit(previousUnit)
                  ? getQuantitySetValue(persistedQuantitySet, previousUnit)
                  : null;
                const persistedNextAmount = persistedQuantitySet && isAllocationUnit(next)
                  ? getQuantitySetValue(persistedQuantitySet, next)
                  : null;
                const convertedNextAmount = isAllocationUnit(previousUnit) && isAllocationUnit(next)
                  ? convertCommercialQuantityUsingSnapshot(
                    positive(amount),
                    previousUnit,
                    next,
                    dimensions,
                    amountSet,
                  ).value
                  : null;
                const nextAmount = persistedPreviousAmount !== null
                  && persistedNextAmount !== null
                  && roundCommercialQuantity(positive(amount)) === persistedPreviousAmount
                  ? persistedNextAmount
                  : convertedNextAmount;
                setPlainUnit(next);
                setAmount(positive(nextAmount, positive(amount)));
                setPieceRounding(null);
                setEditorDirty(true);
                if (!isAllocationUnit(next)) {
                  setMode('none');
                  setPriceUnit(next);
                }
                if (pricingUnit === next) {
                  setPricingUnit(COMMERCIAL_UNITS.find((unit) => unit !== next) || 'pcs');
                }
              }}
            />
          </label>
          <label>
            <span>{t('orders:unitConversion.convertTo')}</span>
            <Select
              data-testid="conversion-target-unit"
              value={mode === 'none' ? 'none' : activeConversionUnit}
              disabled={lockConversionMode || legacyUnitReadOnly}
              getPopupContainer={getModalPopupContainer}
              options={[
                { value: 'none', label: t('orders:unitConversion.noConversionShort') },
                ...pricingUnitOptions.map((unit) => ({ value: unit, label: formatUnit(unit, language) })),
              ]}
              onChange={(next) => {
                setPieceRounding(null);
                setEditorDirty(true);
                if (next === 'none') setMode('none');
                else { setPricingUnit(next as AllocationUnit); setMode('conversion'); }
              }}
            />
          </label>
          <label>
            <span>{t('orders:unitConversion.priceIn')}</span>
            <Select
              data-testid="conversion-price-unit"
              value={priceUnit}
              disabled={lockPricingUnit || legacyUnitReadOnly}
              getPopupContainer={getModalPopupContainer}
              options={(canConvertPhysicalUnit
                ? COMMERCIAL_UNITS.filter((unit) => (
                  !storedQuantitySet || getQuantitySetValue(storedQuantitySet, unit) !== null
                ))
                : [activePhysicalUnit]
              ).map((unit) => ({ value: unit, label: formatUnit(unit, language) }))}
              onChange={(next) => {
                setPriceUnit(next);
                setPieceRounding(null);
                setEditorDirty(true);
              }}
            />
          </label>
        </div>

        <label className="commercial-quantity-modal__amount">
          <span>{t('orders:unitConversion.amountInBaseUnit', { unit: formatUnit(activePhysicalUnit, language) })}</span>
          <InputNumber disabled={lockPhysicalQuantity || legacyUnitReadOnly} min={allowZero ? 0 : 0.001} max={amountMaximum} precision={3} step={0.001} {...POLISH_DECIMAL_INPUT_NUMBER_PROPS} value={amount} onChange={(next) => {
            setAmount(allowZero && Number(next) === 0 ? 0 : positive(next));
            setPieceRounding(null);
            setEditorDirty(true);
          }} />
        </label>

        {result.error ? (
          <Alert type="warning" showIcon message={t('orders:unitConversion.missingDimensions')} />
        ) : (
          <>
            {resultOutOfBounds && (
              <Alert type="warning" showIcon message={outOfBoundsMessage || t('orders:unitConversion.quantityLimit')} />
            )}
            {mode === 'conversion' && (
              <div className="commercial-quantity-modal__equation">
                <div><span>{t('orders:unitConversion.baseQuantity')}</span><strong>{formatQuantity(result.physicalQuantity)} {formatUnit(result.physicalUnit, language)}</strong></div>
                <RightOutlined />
                <div><span>{t('orders:unitConversion.convertedQuantity')}</span><strong>{formatQuantity(result.conversionQuantity || 0)} {formatUnit(result.conversionUnit || activeConversionUnit, language)}</strong></div>
              </div>
            )}
            {fractionalPieceRounding && (
              <div className="commercial-quantity-modal__rounding">
                <div className="commercial-quantity-modal__rounding-head">
                  <strong>{t('orders:unitConversion.fractionalTitle')}</strong>
                </div>
                <p data-testid="conversion-fractional-hint">
                  {t('orders:unitConversion.fractionalDescription', {
                    pieces: `${formatQuantity(fractionalPieceRounding.exactPieces)} ${formatUnit('pcs', language)}`,
                  })}
                </p>
                <div className="commercial-quantity-modal__rounding-options">
                  <button
                    type="button"
                    aria-label={t('orders:unitConversion.roundDown')}
                    disabled={!fractionalPieceRounding.down}
                    className={pieceRounding === 'down' ? 'active' : ''}
                    aria-pressed={pieceRounding === 'down'}
                    onClick={() => setPieceRounding('down')}
                  >
                    <span>{t('orders:unitConversion.roundDown')}</span>
                    <strong>{formatQuantity(fractionalPieceRounding.down?.pieces || 0)} {formatUnit('pcs', language)}</strong>
                    <small>{formatQuantity(fractionalPieceRounding.down?.physicalQuantity || 0)} {formatUnit(baseResult.physicalUnit, language)}</small>
                  </button>
                  <button
                    type="button"
                    aria-label={t('orders:unitConversion.roundUp')}
                    disabled={!fractionalPieceRounding.up}
                    className={pieceRounding === 'up' ? 'active' : ''}
                    aria-pressed={pieceRounding === 'up'}
                    onClick={() => setPieceRounding('up')}
                  >
                    <span>{t('orders:unitConversion.roundUp')}</span>
                    <strong>{formatQuantity(fractionalPieceRounding.up?.pieces || 0)} {formatUnit('pcs', language)}</strong>
                    <small>{formatQuantity(fractionalPieceRounding.up?.physicalQuantity || 0)} {formatUnit(baseResult.physicalUnit, language)}</small>
                  </button>
                </div>
              </div>
            )}
            {pairMismatch && (
              <Alert
                type="error"
                showIcon
                className="commercial-quantity-modal__mismatch"
                data-testid="conversion-pair-mismatch"
                message={t(fractionalPieceRounding
                  ? 'orders:unitConversion.pairMismatchRound'
                  : 'orders:unitConversion.pairMismatch')}
              />
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default CommercialQuantityEditor;
